import { create } from 'zustand';
import { useMemo, type CSSProperties } from 'react';
import { getCalendarThemesConfig, type CalendarThemeDto } from '../api/calendar-themes';
import { getMySettings, patchMySettings } from '../api/users';

/**
 * 캘린더 테마 적용 store (2026-06-07 T5).
 *
 * - catalog: admin 등록 enabled 테마(/calendar-themes-config).
 * - activeKey: 사용자 선택(UserSettings.calendarThemeKey). 없는/비활성 키면 'default' fallback.
 * - vars(): 활성 테마의 --cal-* CSSProperties. CalendarPage/대시보드 위젯 루트에 inline 주입.
 *
 * ★소비측 sanitize(v2 §12-1 2차 방어): 토큰 값이 #hex|var(--x) 아니면 drop → fallback.
 *   (admin write 검증을 통과하지만, fallback 이 invalid 값을 못 막으므로 소비측에서도 재검증.)
 */

const CAL_TOKEN_KEYS = new Set([
  '--cal-grid-bg', '--cal-cell-bg', '--cal-cell-other-bg', '--cal-today-bg', '--cal-border',
  '--cal-header-bg', '--cal-text', '--cal-muted', '--cal-accent',
  '--cal-type-meeting', '--cal-type-personal', '--cal-type-external', '--cal-type-training',
  '--cal-type-todo', '--cal-type-anniversary', '--cal-type-vacation',
]);
const VALUE_RE = /^#[0-9a-fA-F]{3}$|^#[0-9a-fA-F]{6}$|^var\(--[a-z0-9-]+\)$/;

/** tokens → 안전한 CSSProperties(허용 키·값만). 빈 객체면 테마 미적용(=fallback=현행). */
export function sanitizeTokens(tokens: Record<string, string> | undefined): CSSProperties {
  const out: Record<string, string> = {};
  if (!tokens) return out;
  for (const [k, v] of Object.entries(tokens)) {
    if (CAL_TOKEN_KEYS.has(k) && typeof v === 'string' && VALUE_RE.test(v.trim())) out[k] = v.trim();
  }
  return out as CSSProperties;
}

interface CalThemeState {
  catalog: CalendarThemeDto[];
  activeKey: string;
  loaded: boolean;
  loading: boolean;
  ensureLoaded: () => Promise<void>;
  reloadCatalog: () => Promise<void>;
  setActiveKey: (key: string) => void;
  vars: () => CSSProperties;
}

export const useCalendarThemeStore = create<CalThemeState>((set, get) => ({
  catalog: [],
  activeKey: 'default',
  loaded: false,
  loading: false,

  // 최초 1회: 카탈로그 + 사용자 선택 로드. 실패는 무시(=default fallback).
  ensureLoaded: async () => {
    if (get().loaded || get().loading) return;
    set({ loading: true });
    try {
      const [cfg, settings] = await Promise.allSettled([getCalendarThemesConfig(), getMySettings()]);
      const catalog = cfg.status === 'fulfilled' ? cfg.value.themes : [];
      const activeKey = settings.status === 'fulfilled' ? (settings.value.calendarThemeKey || 'default') : 'default';
      set({ catalog, activeKey, loaded: true, loading: false });
    } catch {
      set({ loaded: true, loading: false });
    }
  },

  // 프로필 테마 탭 진입 시 최신 카탈로그 재조회(admin 변경 반영 — v2 §12-10).
  reloadCatalog: async () => {
    try { const cfg = await getCalendarThemesConfig(); set({ catalog: cfg.themes }); } catch { /* keep */ }
  },

  // 선택: 즉시 store 반영(화면 즉시) + 서버 영속(debounce 없이 단발 — 선택은 드묾).
  setActiveKey: (key) => {
    set({ activeKey: key });
    void patchMySettings({ calendarThemeKey: key }).catch(() => { /* 비치명 — 다음 부트에 재동기 */ });
  },

  // 활성 테마 tokens → sanitize 된 CSS 변수. 없는/비활성 키 → {} (fallback=현행).
  vars: () => {
    const { catalog, activeKey } = get();
    if (!activeKey || activeKey === 'default') return {};
    const t = catalog.find((c) => c.theme_key === activeKey);
    return t ? sanitizeTokens(t.tokens) : {};
  },
}));

/** 캘린더 화면·대시보드 위젯 루트에 적용할 --cal-* CSSProperties (반응형, 메모이즈). */
export function useCalendarThemeVars(): CSSProperties {
  const activeKey = useCalendarThemeStore((s) => s.activeKey);
  const catalog = useCalendarThemeStore((s) => s.catalog);
  return useMemo(() => {
    if (!activeKey || activeKey === 'default') return {};
    const t = catalog.find((c) => c.theme_key === activeKey);
    return t ? sanitizeTokens(t.tokens) : {};
  }, [activeKey, catalog]);
}
